import React, { useEffect } from "react";
import confetti from "canvas-confetti";
import PlanCard from "../Cards/PlanCard";
import OnboardingLayout from "./OnboardingLayout";
import { CheckCircleIcon } from "@chakra-ui/icons";

interface PlanLoadedProps {
  language: string;
  dailyStudy: number;
  fluent: string;
  challenge: string;
  goal: string;
}

const PlanLoaded: React.FC<PlanLoadedProps> = ({
  language,
  dailyStudy,
  fluent,
  challenge,
  goal,
}) => {
  useEffect(() => {
    const duration = 3500;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 20, spread: 300, ticks: 50, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 40 * (timeLeft / duration);
      confetti(
        Object.assign({}, defaults, {
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        })
      );
      confetti(
        Object.assign({}, defaults, {
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        })
      );
    }, 250);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="pt-4 pb-8">
      <CheckCircleIcon boxSize={8} color="var(--color-green)" />
      <OnboardingLayout
        title={
          <span className="flex text-center text-xl items-center justify-center self-center">
            Congratulations!
            <br />
            Your custom plan is waiting for you...
          </span>
        }
        subtitle=""
        classname="text-center items-center justify-center"
      >
        <div className="flex flex-col w-full h-full items-center justify-start gap-4">
          <div className="px-4 py-2 rounded-full bg-[#DBEAFE] text-[var(--color-blue)] mb-4 flex flex-wrap justify-center">
            <span>🇪🇸 Reach your {language} goals by&nbsp;</span>
            <span>{fluent}!</span>
          </div>
          <div className="flex flex-col w-full h-full items-center gap-3 -mt-2">
            <PlanCard
              className="fade-in"
              style={{ animationDelay: "0.5s", opacity: 0 }}
              title={`${dailyStudy}-minute bite-sized lessons`}
              subtitle="Perfect for your busy schedule"
              icon="⏰"
            />

            <PlanCard
              className="fade-in"
              style={{ animationDelay: "1s", opacity: 0 }}
              title={
                goal === "Travel"
                  ? "Travel with confidence"
                  : goal === "Work"
                  ? "Career advancement"
                  : goal === "Friends & Family"
                  ? "Deeper connections"
                  : goal === "School"
                  ? "Academic excellence"
                  : goal === "Self improvement"
                  ? "Personal growth"
                  : goal === "Romance"
                  ? "Romantic connections"
                  : "Personal achievement"
              }
              subtitle={
                goal === "Travel"
                  ? "87% of students report increased confidence while traveling"
                  : goal === "Work"
                  ? "73% of users report career advancement from language skills"
                  : goal === "Friends & Family"
                  ? "92% build meaningful family conversations within 3 months"
                  : goal === "School"
                  ? "31% higher average scores in academic settings"
                  : goal === "Self improvement"
                  ? "88% report increased cognitive abilities"
                  : goal === "Romance"
                  ? "Connect on a deeper level with cultural dating insights"
                  : "Achieve your personal language goals with structured guidance."
              }
              icon={
                goal === "Travel"
                  ? "🌍"
                  : goal === "Work"
                  ? "💼"
                  : goal === "Friends & Family"
                  ? "👯"
                  : goal === "School"
                  ? "📚"
                  : goal === "Self improvement"
                  ? "🧠"
                  : goal === "Romance"
                  ? "❤️"
                  : "🌟"
              }
            />

            <PlanCard
              className="fade-in"
              style={{ animationDelay: "1.5s", opacity: 0 }}
              title={`Personal accountability partner`}
              subtitle={`Parrot will help you overcome ${
                challenge.toLowerCase() === "no time"
                  ? "your time constraints"
                  : challenge.toLowerCase() === "where to start"
                  ? "your uncertainty about getting started"
                  : challenge.toLowerCase() === "staying motivated"
                  ? "your motivation challenges"
                  : challenge.toLowerCase() === "grammar issues"
                  ? "your grammar challenges"
                  : challenge.toLowerCase() === "speaking practice"
                  ? "your speaking practice needs"
                  : challenge.toLowerCase() === "vocabulary overload"
                  ? "your vocabulary challenges"
                  : `the challenges you face with ${challenge.toLowerCase()}`
              }.`}
              icon="🦜"
            />

            <PlanCard
              className="fade-in"
              style={{ animationDelay: "2s", opacity: 0 }}
              title={`92% success rate`}
              subtitle="Join our students who successfully reach their language goals."
              icon="📈"
            />
            <PlanCard
              className="fade-in"
              style={{ animationDelay: "2.5s", opacity: 0 }}
              title={`1000+ hours of immersive content`}
              subtitle="Parrot finds the best content to immerse you in the language. Similar to TikTok."
              icon="🎬"
            />
          </div>
        </div>
      </OnboardingLayout>
    </div>
  );
};

export default PlanLoaded;
